import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, API } from "@/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Check, X as XIcon, Paperclip, Download, Wallet, Cog, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import { inr, fmtDate } from "@/utils";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EXPENSE_CATEGORIES_FALLBACK = ["Construction", "Material Purchase", "Material Consumption", "Direct Site Purchase", "Labour", "Marketing", "Salary", "Legal", "Land Owner Share"];
const PAYMENT_MODES = [
  { v: "bank_transfer", l: "Bank Transfer" }, { v: "cheque", l: "Cheque" }, { v: "cash", l: "Cash" },
  { v: "upi", l: "UPI" }, { v: "rtgs", l: "RTGS/NEFT" }, { v: "credit_card", l: "Credit Card" },
  { v: "petty_cash", l: "Petty Cash" },
];

function StatusBadge({ s }) {
  const map = {
    approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
    pending: "bg-amber-100 text-amber-800 border-amber-300",
    rejected: "bg-red-100 text-red-800 border-red-300",
    on_hold: "bg-zinc-100 text-zinc-700 border-zinc-300",
  };
  return <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border rounded ${map[s] || map.approved}`}>{s || "approved"}</span>;
}

export default function Finance() {
  const { user } = useAuth();
  const isManager = user?.role === "Admin" || user?.role === "Manager";
  const isAdmin = user?.role === "Admin";
  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const [projectId, setProjectId] = useState("");

  // Filters (expense tab)
  const [filter, setFilter] = useState({ status: "", category: "", payment_mode: "", date_from: "", date_to: "" });

  const expenseQuery = useMemo(() => {
    const p = {};
    if (projectId) p.project_id = projectId;
    Object.entries(filter).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [projectId, filter]);

  const { data: expenses = [] } = useQuery({
    queryKey: ["expenses", expenseQuery],
    queryFn: async () => (await api.get("/expenses", { params: expenseQuery })).data,
  });
  const { data: expSummary } = useQuery({
    queryKey: ["expense-summary", projectId],
    queryFn: async () => (await api.get("/expenses/summary", { params: projectId ? { project_id: projectId } : {} })).data,
  });
  const { data: incomes = [] } = useQuery({
    queryKey: ["incomes", projectId],
    queryFn: async () => (await api.get("/incomes", { params: projectId ? { project_id: projectId } : {} })).data,
  });
  const { data: loans = [] } = useQuery({
    queryKey: ["loans", projectId],
    queryFn: async () => (await api.get("/loans", { params: projectId ? { project_id: projectId } : {} })).data,
  });
  const { data: pnl } = useQuery({
    queryKey: ["pnl-page", projectId],
    queryFn: async () => (await api.get("/dashboard/pnl", { params: projectId ? { project_id: projectId } : {} })).data,
  });
  const { data: categoryList = [] } = useQuery({
    queryKey: ["expense-categories"],
    queryFn: async () => (await api.get("/expense-categories")).data,
  });
  const { data: finSettings } = useQuery({
    queryKey: ["finance-settings"],
    queryFn: async () => (await api.get("/finance/settings")).data,
  });
  const { data: pettyAccounts = [] } = useQuery({
    queryKey: ["petty-accounts", projectId],
    queryFn: async () => (await api.get("/petty-cash/accounts", { params: projectId ? { project_id: projectId } : {} })).data,
  });

  const attachThreshold = finSettings?.expense_attachment_threshold ?? 5000;
  const approvalThreshold = finSettings?.expense_approval_threshold ?? 50000;

  const EXPENSE_CATEGORIES = useMemo(() => {
    const active = categoryList.filter((c) => c.active).map((c) => c.name);
    return active.length ? active : EXPENSE_CATEGORIES_FALLBACK;
  }, [categoryList]);

  // ------------- Add expense state ---------------
  const [expOpen, setExpOpen] = useState(false);
  const [expSearch, setExpSearch] = useState("");
  const [expPage, setExpPage] = useState(1);
  const [expSize, setExpSize] = useState(25);

  const filteredExpenses = useMemo(() => {
    if (!expSearch.trim()) return expenses;
    const q = expSearch.toLowerCase();
    return expenses.filter((e) =>
      (e.vendor_name || "").toLowerCase().includes(q) ||
      (e.description || "").toLowerCase().includes(q) ||
      (e.category || "").toLowerCase().includes(q)
    );
  }, [expenses, expSearch]);

  const pagedExpenses = useMemo(
    () => filteredExpenses.slice((expPage - 1) * expSize, expPage * expSize),
    [filteredExpenses, expPage, expSize]
  );

  const expenseTotals = useMemo(() => {
    const total = filteredExpenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const pageTotal = pagedExpenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    return { total, pageTotal };
  }, [filteredExpenses, pagedExpenses]);

  const EMPTY_EXP = {
    category: "Construction", vendor_name: "", amount: 0, gst_amount: 0, tds_amount: 0,
    date: new Date().toISOString().slice(0, 10), description: "", is_land_owner_payment: false,
    payment_mode: "bank_transfer", invoice_number: "", reference_no: "", cost_center: "", notes: "",
    petty_cash_account_id: "", supporting_documents: [],
  };
  const [expForm, setExpForm] = useState(EMPTY_EXP);
  const [uploading, setUploading] = useState(false);
  const [editExp, setEditExp] = useState(null);
  const [incOpen, setIncOpen] = useState(false);
  const [incForm, setIncForm] = useState({ source: "Instalment Collection", amount: 0, gst_amount: 0, date: new Date().toISOString().slice(0, 10), description: "" });
  const [loanOpen, setLoanOpen] = useState(false);
  const [loanForm, setLoanForm] = useState({ lender_name: "", lender_type: "bank", principal: 0, interest_rate: 10.5, tenure_months: 60, emi_amount: 0, outstanding: 0, disbursement_date: "" });

  async function uploadBill(e, target = "new") {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!projectId && target === "new") { toast.error("Select project first"); e.target.value = ""; return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("project_id", target === "new" ? projectId : editExp.project_id);
    fd.append("category", "receipt");
    try {
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (target === "new") {
        setExpForm((f) => ({ ...f, supporting_documents: [...(f.supporting_documents || []), data.id] }));
      } else {
        setEditExp((f) => ({ ...f, supporting_documents: [...(f.supporting_documents || []), data.id] }));
      }
      toast.success(`Bill attached: ${file.name}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  }

  function downloadDoc(id) {
    const token = localStorage.getItem("param_token");
    window.open(`${API}/documents/${id}/download?auth=${encodeURIComponent(token)}`, "_blank");
  }

  async function saveExp() {
    if (!projectId) return toast.error("Select a project");
    const amt = +expForm.amount;
    const attachmentExempt = expForm.payment_mode === "cash" || expForm.payment_mode === "petty_cash";
    if (!attachmentExempt && amt >= attachThreshold && !(expForm.supporting_documents || []).length) {
      return toast.error(`Bill/receipt attachment mandatory for amounts ≥ ₹${attachThreshold.toLocaleString("en-IN")}`);
    }
    if (expForm.payment_mode === "petty_cash" && !expForm.petty_cash_account_id) {
      return toast.error("Select a Petty Cash account");
    }
    try {
      await api.post("/expenses", { ...expForm, project_id: projectId, amount: amt, gst_amount: +expForm.gst_amount, tds_amount: +expForm.tds_amount });
      toast.success(amt >= approvalThreshold && !isManager ? "Expense submitted for approval" : "Expense recorded");
      ["expenses", "expense-summary", "pnl-page", "petty-accounts", "dashboard-kpi"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setExpOpen(false);
      setExpForm(EMPTY_EXP);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function saveEditExp() {
    if (!editExp?.id) return;
    try {
      const payload = { ...editExp, amount: +editExp.amount, gst_amount: +editExp.gst_amount, tds_amount: +editExp.tds_amount };
      const reason = editExp._reason || "";
      delete payload.id; delete payload._id;
      if (reason) payload._reason = reason;
      await api.patch(`/expenses/${editExp.id}`, payload);
      toast.success("Expense updated");
      ["expenses", "expense-summary", "pnl-page", "dashboard-kpi"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setEditExp(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  }

  async function deleteExp(exp) {
    if (!window.confirm(`Delete expense of ₹${exp.amount}?`)) return;
    try {
      await api.delete(`/expenses/${exp.id}`);
      toast.success("Expense deleted");
      ["expenses", "expense-summary", "pnl-page"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setEditExp(null);
    } catch { toast.error("Delete failed"); }
  }

  async function approveExp(id) {
    try {
      await api.post(`/expenses/${id}/approve`);
      toast.success("Expense approved");
      ["expenses", "expense-summary", "pnl-page", "petty-accounts"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function rejectExp(id) {
    const reason = window.prompt("Reason for rejection?", "");
    if (reason === null) return;
    try {
      await api.post(`/expenses/${id}/reject`, { reason });
      toast.success("Expense rejected");
      ["expenses", "expense-summary", "pnl-page"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function saveInc() {
    if (!projectId) return toast.error("Select a project");
    try {
      await api.post("/incomes", { ...incForm, project_id: projectId, amount: +incForm.amount, gst_amount: +incForm.gst_amount });
      toast.success("Income recorded");
      qc.invalidateQueries({ queryKey: ["incomes"] }); qc.invalidateQueries({ queryKey: ["pnl-page"] });
      setIncOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function saveLoan() {
    if (!projectId) return toast.error("Select a project");
    try {
      await api.post("/loans", { ...loanForm, project_id: projectId, principal: +loanForm.principal, interest_rate: +loanForm.interest_rate, tenure_months: +loanForm.tenure_months, emi_amount: +loanForm.emi_amount, outstanding: +loanForm.outstanding });
      toast.success("Loan added");
      qc.invalidateQueries({ queryKey: ["loans"] }); qc.invalidateQueries({ queryKey: ["pnl-page"] });
      setLoanOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  const kpiRow = (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Revenue</div><div className="kpi-num mt-1 text-emerald-700">{inr(pnl?.total_revenue)}</div></CardContent></Card>
      <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Expense</div><div className="kpi-num mt-1 text-red-600">{inr(pnl?.total_expense)}</div></CardContent></Card>
      <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Gross Profit</div><div className="kpi-num mt-1">{inr(pnl?.gross_profit)}</div></CardContent></Card>
      <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Loan Outstanding</div><div className="kpi-num mt-1">{inr(pnl?.loan_outstanding)}</div></CardContent></Card>
      <Card className="bg-white border-zinc-200"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500">Land Owner Paid</div><div className="kpi-num mt-1">{inr(pnl?.land_owner_share_paid)}</div></CardContent></Card>
    </div>
  );

  const pendingCount = expSummary?.by_status?.pending?.count || 0;
  const pendingAmount = expSummary?.by_status?.pending?.amount || 0;

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Finance</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Finance &amp; P&amp;L</h1>
          <p className="text-sm text-zinc-500 mt-1">Project-wise books — income, expenses (with approval + bills), petty cash, loans, GST &amp; TDS ready.</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && <SettingsButton finSettings={finSettings} />}
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-64" data-testid="finance-project-filter"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {kpiRow}

      <Tabs defaultValue="expenses">
        <TabsList className="bg-white border border-zinc-200">
          <TabsTrigger value="incomes" data-testid="fin-tab-income">Income</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="fin-tab-expense">Expenses</TabsTrigger>
          <TabsTrigger value="petty" data-testid="fin-tab-petty">Petty Cash</TabsTrigger>
          <TabsTrigger value="loans" data-testid="fin-tab-loans">Loans</TabsTrigger>
        </TabsList>

        {/* INCOME TAB (unchanged) */}
        <TabsContent value="incomes" className="mt-4">
          <Card className="bg-white border-zinc-200">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="font-heading text-base">Income Ledger</CardTitle>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      if (!window.confirm("Re-sync every receipt's revenue-recognized flag against its customer's Sale Deed status?\n\nUse this if a Sale Deed was registered but revenue is not showing.")) return;
                      try {
                        const { data } = await api.post("/admin/reconcile-revenue");
                        toast.success(`Sync done · ${data.receipts_marked_recognized} moved to revenue, ${data.receipts_reverted_to_advance} back to advances`);
                        ["pnl-page", "dashboard-kpi", "incomes", "customers-wf", "dashboard-units"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
                      } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
                    }}
                    className="text-xs"
                    data-testid="sync-revenue-btn"
                    title="Rebuild revenue recognition flags from Sale Deed state"
                  >
                    ↻ Sync Revenue
                  </Button>
                )}
                <Dialog open={incOpen} onOpenChange={setIncOpen}>
                  <DialogTrigger asChild><Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-income-btn"><Plus size={14} className="mr-1" /> Add Income</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-heading">Record Income</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Source</Label><Input value={incForm.source} onChange={(e) => setIncForm({ ...incForm, source: e.target.value })} /></div>
                    <div><Label>Date</Label><Input type="date" value={incForm.date} onChange={(e) => setIncForm({ ...incForm, date: e.target.value })} /></div>
                    <div><Label>Amount (₹)</Label><Input type="number" value={incForm.amount} onChange={(e) => setIncForm({ ...incForm, amount: e.target.value })} /></div>
                    <div><Label>GST (₹)</Label><Input type="number" value={incForm.gst_amount} onChange={(e) => setIncForm({ ...incForm, gst_amount: e.target.value })} /></div>
                    <div className="col-span-2"><Label>Description</Label><Input value={incForm.description} onChange={(e) => setIncForm({ ...incForm, description: e.target.value })} /></div>
                  </div>
                  <DialogFooter><Button onClick={saveInc} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-income">Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader className="bg-zinc-50"><TableRow>
                  <TableHead>Date</TableHead><TableHead>Source</TableHead><TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead><TableHead className="text-right">GST</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {incomes.map((i) => (
                    <TableRow key={i.id} className="data-row">
                      <TableCell className="font-mono text-xs">{fmtDate(i.date)}</TableCell>
                      <TableCell>{i.source}</TableCell>
                      <TableCell className="text-xs text-zinc-500">{i.description}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-700">{inr(i.amount)}</TableCell>
                      <TableCell className="text-right font-mono">{inr(i.gst_amount)}</TableCell>
                    </TableRow>
                  ))}
                  {!incomes.length && <TableRow><TableCell colSpan={5} className="text-center text-sm text-zinc-500 py-10">No income entries.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* EXPENSES TAB — ENHANCED */}
        <TabsContent value="expenses" className="mt-4 space-y-4">
          {/* Analytics strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="bg-white border-zinc-200"><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Expenses</div>
              <div className="text-xl font-bold font-mono mt-1">{inr(expSummary?.total || 0)}</div>
            </CardContent></Card>
            <Card className="bg-white border-amber-200"><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 flex items-center gap-1">Pending Approval</div>
              <div className="text-xl font-bold font-mono mt-1 text-amber-800" data-testid="expense-pending-count">{pendingCount} · {inr(pendingAmount)}</div>
            </CardContent></Card>
            <Card className="bg-white border-zinc-200"><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Top Category</div>
              <div className="text-sm mt-1 truncate">{expSummary?.by_category?.[0]?.category || "—"}</div>
              <div className="text-xs font-mono text-zinc-500">{inr(expSummary?.by_category?.[0]?.amount || 0)}</div>
            </CardContent></Card>
            <Card className="bg-white border-zinc-200"><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Rules</div>
              <div className="text-xs mt-1">Bill mandatory ≥ <b>{inr(attachThreshold)}</b> <span className="text-zinc-400">(except cash / petty cash)</span></div>
              <div className="text-xs">Approval ≥ <b>{inr(approvalThreshold)}</b></div>
            </CardContent></Card>
          </div>

          <Card className="bg-white border-zinc-200">
            <CardHeader className="flex-col md:flex-row md:items-center md:justify-between gap-2">
              <CardTitle className="font-heading text-base">Expense Ledger</CardTitle>
              <div className="flex flex-wrap gap-2 items-center">
                <Select value={filter.status || " "} onValueChange={(v) => setFilter({ ...filter, status: v.trim() })}>
                  <SelectTrigger className="w-32 h-8 text-xs" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filter.category || " "} onValueChange={(v) => setFilter({ ...filter, category: v.trim() })}>
                  <SelectTrigger className="w-40 h-8 text-xs" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">All Categories</SelectItem>
                    {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filter.payment_mode || " "} onValueChange={(v) => setFilter({ ...filter, payment_mode: v.trim() })}>
                  <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="Payment" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">All Modes</SelectItem>
                    {PAYMENT_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value })} className="w-36 h-8 text-xs" placeholder="From" />
                <Input type="date" value={filter.date_to} onChange={(e) => setFilter({ ...filter, date_to: e.target.value })} className="w-36 h-8 text-xs" placeholder="To" />
                <div className="relative">
                  <Input
                    placeholder="Search vendor, description…"
                    value={expSearch}
                    onChange={(e) => setExpSearch(e.target.value)}
                    className="w-56 h-8 text-xs pl-3"
                    data-testid="expense-search"
                  />
                </div>
                <Dialog open={expOpen} onOpenChange={(o) => { setExpOpen(o); if (!o) setExpForm(EMPTY_EXP); }}>
                  <DialogTrigger asChild><Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-expense-btn"><Plus size={14} className="mr-1" /> Add Expense</Button></DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle className="font-heading">Record Expense</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Category</Label>
                        <Select value={expForm.category} onValueChange={(v) => setExpForm({ ...expForm, category: v })}>
                          <SelectTrigger data-testid="new-expense-category"><SelectValue /></SelectTrigger>
                          <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label>Vendor / Payee</Label><Input value={expForm.vendor_name} onChange={(e) => setExpForm({ ...expForm, vendor_name: e.target.value })} data-testid="new-expense-vendor" /></div>
                      <div><Label>Amount (₹)</Label><Input type="number" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} data-testid="new-expense-amount" /></div>
                      <div><Label>Date</Label><Input type="date" value={expForm.date} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} /></div>
                      <div><Label>GST (₹)</Label><Input type="number" value={expForm.gst_amount} onChange={(e) => setExpForm({ ...expForm, gst_amount: e.target.value })} /></div>
                      <div><Label>TDS (₹)</Label><Input type="number" value={expForm.tds_amount} onChange={(e) => setExpForm({ ...expForm, tds_amount: e.target.value })} /></div>
                      <div><Label>Payment Mode</Label>
                        <Select value={expForm.payment_mode} onValueChange={(v) => setExpForm({ ...expForm, payment_mode: v })}>
                          <SelectTrigger data-testid="new-expense-payment"><SelectValue /></SelectTrigger>
                          <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {expForm.payment_mode === "petty_cash" && (
                        <div><Label>Petty Cash Account</Label>
                          <Select value={expForm.petty_cash_account_id} onValueChange={(v) => setExpForm({ ...expForm, petty_cash_account_id: v })}>
                            <SelectTrigger data-testid="new-expense-petty"><SelectValue placeholder="Choose account" /></SelectTrigger>
                            <SelectContent>
                              {pettyAccounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · Bal {inr(a.balance)}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div><Label>Invoice / Bill No.</Label><Input value={expForm.invoice_number} onChange={(e) => setExpForm({ ...expForm, invoice_number: e.target.value })} /></div>
                      <div><Label>Reference No.</Label><Input value={expForm.reference_no} onChange={(e) => setExpForm({ ...expForm, reference_no: e.target.value })} /></div>
                      <div><Label>Cost Centre</Label><Input value={expForm.cost_center} onChange={(e) => setExpForm({ ...expForm, cost_center: e.target.value })} /></div>
                      <div className="col-span-2 flex items-center gap-2">
                        <Checkbox id="lo" checked={expForm.is_land_owner_payment} onCheckedChange={(v) => setExpForm({ ...expForm, is_land_owner_payment: !!v })} />
                        <Label htmlFor="lo">This is a land-owner payment (JDA)</Label>
                      </div>
                      <div className="col-span-2"><Label>Description</Label><Input value={expForm.description} onChange={(e) => setExpForm({ ...expForm, description: e.target.value })} /></div>

                      {/* Bill attachment */}
                      <div className="col-span-2 border border-dashed border-zinc-300 rounded-md p-3 bg-zinc-50">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <div className="text-xs font-semibold flex items-center gap-1"><Paperclip size={12} /> Bill / Receipt Attachments</div>
                            <div className="text-[10px] text-zinc-500">
                              {expForm.payment_mode === "cash" || expForm.payment_mode === "petty_cash"
                                ? "Optional for cash / petty cash payments — attach if you have a receipt."
                                : `Mandatory for amounts ≥ ${inr(attachThreshold)}. PDF / image accepted.`}
                            </div>
                          </div>
                          <label className="inline-flex items-center gap-1 px-3 h-8 bg-white border border-zinc-300 hover:bg-zinc-100 rounded-md text-xs cursor-pointer">
                            {uploading ? "Uploading…" : "+ Add file"}
                            <input type="file" hidden onChange={(e) => uploadBill(e, "new")} disabled={uploading} data-testid="new-expense-bill" />
                          </label>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {(expForm.supporting_documents || []).map((id, idx) => (
                            <button key={id} onClick={() => downloadDoc(id)} className="inline-flex items-center gap-1 text-[11px] bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded text-emerald-800 hover:bg-emerald-100">
                              <Download size={10} /> Bill #{idx + 1}
                            </button>
                          ))}
                          {!(expForm.supporting_documents || []).length && <div className="text-[11px] text-zinc-400">No files yet.</div>}
                        </div>
                      </div>
                    </div>
                    <DialogFooter><Button onClick={saveExp} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-expense">Save</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader className="bg-zinc-50"><TableRow>
                  <TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Vendor</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Bills</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {pagedExpenses.map((e) => (
                    <TableRow key={e.id} className="data-row" data-testid={`expense-row-${e.id}`}>
                      <TableCell className="font-mono text-xs">{fmtDate(e.date)}</TableCell>
                      <TableCell>{e.category}{e.is_land_owner_payment && <span className="ml-2 text-[10px] bg-amber-100 text-amber-800 border border-amber-300 px-1.5 rounded">JDA</span>}</TableCell>
                      <TableCell className="text-xs">{e.vendor_name}</TableCell>
                      <TableCell className="text-[10px] uppercase text-zinc-600">{e.payment_mode?.replace("_", " ")}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{inr(e.amount)}</TableCell>
                      <TableCell><StatusBadge s={e.approval_status} /></TableCell>
                      <TableCell className="text-center">
                        {(e.supporting_documents || []).length ? (
                          <button className="inline-flex items-center gap-0.5 text-xs text-emerald-700 hover:underline" onClick={() => downloadDoc(e.supporting_documents[0])}>
                            <Paperclip size={11} />{e.supporting_documents.length}
                          </button>
                        ) : <span className="text-[10px] text-zinc-400">—</span>}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {isManager && e.approval_status === "pending" && (
                          <>
                            <Button size="sm" variant="outline" className="mr-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => approveExp(e.id)} data-testid={`approve-expense-${e.id}`}>
                              <Check size={12} />
                            </Button>
                            <Button size="sm" variant="outline" className="mr-1 text-red-600 border-red-300 hover:bg-red-50" onClick={() => rejectExp(e.id)} data-testid={`reject-expense-${e.id}`}>
                              <XIcon size={12} />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setEditExp({ ...e, _reason: "" })} data-testid={`edit-expense-${e.id}`}>
                          <Pencil size={12} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!expenses.length && <TableRow><TableCell colSpan={8} className="text-center text-sm text-zinc-500 py-10">No expenses match filters.</TableCell></TableRow>}
                  {!!filteredExpenses.length && (
                    <TableRow className="bg-zinc-100/80 border-t-2 border-zinc-300 hover:bg-zinc-100/80" data-testid="expense-total-row">
                      <TableCell colSpan={4} className="text-right text-[11px] uppercase tracking-widest font-semibold text-zinc-600">
                        Total ({filteredExpenses.length} expense{filteredExpenses.length === 1 ? "" : "s"})
                        <span className="ml-2 text-[10px] normal-case text-zinc-500 tracking-normal">
                          Page total {inr(expenseTotals.pageTotal)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold text-red-700" data-testid="expense-total-amount">
                        {inr(expenseTotals.total)}
                      </TableCell>
                      <TableCell colSpan={3} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {(() => {
                const filteredCount = filteredExpenses.length;
                const pageCount = Math.max(1, Math.ceil(filteredCount / expSize));
                if (!filteredCount) return null;
                const first = (expPage - 1) * expSize + 1;
                const last = Math.min(expPage * expSize, filteredCount);
                return (
                  <div className="flex items-center justify-between gap-3 px-4 py-2 border-t border-zinc-200 bg-zinc-50/50 text-xs" data-testid="expense-pagination">
                    <div className="text-zinc-500">
                      Showing <b className="font-mono text-zinc-800">{first}-{last}</b> of <b className="font-mono text-zinc-800">{filteredCount}</b>
                      <span className="ml-3 pl-3 border-l border-zinc-300">
                        Filtered Total <b className="font-mono text-red-700" data-testid="expense-total-badge">{inr(expenseTotals.total)}</b>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setExpPage(Math.max(1, expPage - 1))} disabled={expPage <= 1} className="p-1 rounded border border-zinc-300 bg-white disabled:opacity-40 hover:bg-zinc-100" data-testid="expense-page-prev">‹</button>
                      <span className="font-mono px-2">{expPage} / {pageCount}</span>
                      <button onClick={() => setExpPage(Math.min(pageCount, expPage + 1))} disabled={expPage >= pageCount} className="p-1 rounded border border-zinc-300 bg-white disabled:opacity-40 hover:bg-zinc-100" data-testid="expense-page-next">›</button>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PETTY CASH TAB */}
        <TabsContent value="petty" className="mt-4">
          <PettyCashPanel projects={projects} projectId={projectId} accounts={pettyAccounts} />
        </TabsContent>

        {/* LOANS TAB (unchanged) */}
        <TabsContent value="loans" className="mt-4">
          <Card className="bg-white border-zinc-200">
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="font-heading text-base">Loans</CardTitle>
              <Dialog open={loanOpen} onOpenChange={setLoanOpen}>
                <DialogTrigger asChild><Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-loan-btn"><Plus size={14} className="mr-1" /> Add Loan</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-heading">Add Loan</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Lender</Label><Input value={loanForm.lender_name} onChange={(e) => setLoanForm({ ...loanForm, lender_name: e.target.value })} /></div>
                    <div><Label>Type</Label>
                      <Select value={loanForm.lender_type} onValueChange={(v) => setLoanForm({ ...loanForm, lender_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bank">Bank</SelectItem><SelectItem value="private">Private</SelectItem>
                          <SelectItem value="director">Director</SelectItem><SelectItem value="partner">Partner</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Principal (₹)</Label><Input type="number" value={loanForm.principal} onChange={(e) => setLoanForm({ ...loanForm, principal: e.target.value })} /></div>
                    <div><Label>Interest Rate (%)</Label><Input type="number" step="0.01" value={loanForm.interest_rate} onChange={(e) => setLoanForm({ ...loanForm, interest_rate: e.target.value })} /></div>
                    <div><Label>Tenure (months)</Label><Input type="number" value={loanForm.tenure_months} onChange={(e) => setLoanForm({ ...loanForm, tenure_months: e.target.value })} /></div>
                    <div><Label>EMI (₹)</Label><Input type="number" value={loanForm.emi_amount} onChange={(e) => setLoanForm({ ...loanForm, emi_amount: e.target.value })} /></div>
                    <div><Label>Outstanding (₹)</Label><Input type="number" value={loanForm.outstanding} onChange={(e) => setLoanForm({ ...loanForm, outstanding: e.target.value })} /></div>
                    <div><Label>Disbursement</Label><Input type="date" value={loanForm.disbursement_date} onChange={(e) => setLoanForm({ ...loanForm, disbursement_date: e.target.value })} /></div>
                  </div>
                  <DialogFooter><Button onClick={saveLoan} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-loan">Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader className="bg-zinc-50"><TableRow>
                  <TableHead>Lender</TableHead><TableHead>Type</TableHead>
                  <TableHead className="text-right">Principal</TableHead><TableHead className="text-right">EMI</TableHead>
                  <TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Outstanding</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {loans.map((l) => (
                    <TableRow key={l.id} className="data-row">
                      <TableCell className="font-medium">{l.lender_name}</TableCell>
                      <TableCell className="uppercase text-xs">{l.lender_type}</TableCell>
                      <TableCell className="text-right font-mono">{inr(l.principal)}</TableCell>
                      <TableCell className="text-right font-mono">{inr(l.emi_amount)}</TableCell>
                      <TableCell className="text-right font-mono">{l.interest_rate}%</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{inr(l.outstanding)}</TableCell>
                    </TableRow>
                  ))}
                  {!loans.length && <TableRow><TableCell colSpan={6} className="text-center text-sm text-zinc-500 py-10">No loans recorded.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Expense Dialog */}
      <Dialog open={!!editExp} onOpenChange={(o) => !o && setEditExp(null)}>
        <DialogContent className="max-w-2xl" data-testid="edit-expense-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit Expense</DialogTitle></DialogHeader>
          {editExp && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={(editExp.date || "").slice(0, 10)} onChange={(e) => setEditExp({ ...editExp, date: e.target.value })} data-testid="ee-date" /></div>
              <div><Label>Category</Label>
                <Select value={editExp.category} onValueChange={(v) => setEditExp({ ...editExp, category: v })}>
                  <SelectTrigger data-testid="ee-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Project</Label>
                <Select value={editExp.project_id} onValueChange={(v) => setEditExp({ ...editExp, project_id: v })}>
                  <SelectTrigger data-testid="ee-project"><SelectValue /></SelectTrigger>
                  <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Vendor</Label><Input value={editExp.vendor_name || ""} onChange={(e) => setEditExp({ ...editExp, vendor_name: e.target.value })} data-testid="ee-vendor" /></div>
              <div><Label>Amount (₹)</Label><Input type="number" value={editExp.amount} onChange={(e) => setEditExp({ ...editExp, amount: e.target.value })} data-testid="ee-amount" /></div>
              <div><Label>GST (₹)</Label><Input type="number" value={editExp.gst_amount} onChange={(e) => setEditExp({ ...editExp, gst_amount: e.target.value })} /></div>
              <div><Label>TDS (₹)</Label><Input type="number" value={editExp.tds_amount} onChange={(e) => setEditExp({ ...editExp, tds_amount: e.target.value })} /></div>
              <div><Label>Payment Mode</Label>
                <Select value={editExp.payment_mode || "bank_transfer"} onValueChange={(v) => setEditExp({ ...editExp, payment_mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Invoice / Bill No.</Label><Input value={editExp.invoice_number || ""} onChange={(e) => setEditExp({ ...editExp, invoice_number: e.target.value })} data-testid="ee-invoice" /></div>
              <div><Label>Cost Centre</Label><Input value={editExp.cost_center || ""} onChange={(e) => setEditExp({ ...editExp, cost_center: e.target.value })} /></div>
              <div><Label>Approval Status</Label>
                <Select value={editExp.approval_status || "approved"} onValueChange={(v) => setEditExp({ ...editExp, approval_status: v })}>
                  <SelectTrigger data-testid="ee-approval"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Checkbox id="ee-lo" checked={!!editExp.is_land_owner_payment} onCheckedChange={(v) => setEditExp({ ...editExp, is_land_owner_payment: !!v })} />
                <Label htmlFor="ee-lo">This is a land-owner payment (JDA)</Label>
              </div>
              <div className="col-span-2"><Label>Description</Label><Input value={editExp.description || ""} onChange={(e) => setEditExp({ ...editExp, description: e.target.value })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Input value={editExp.notes || ""} onChange={(e) => setEditExp({ ...editExp, notes: e.target.value })} /></div>

              {/* Attachments in edit */}
              <div className="col-span-2 border border-dashed border-zinc-300 rounded-md p-3 bg-zinc-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold flex items-center gap-1"><Paperclip size={12} /> Bill Attachments</div>
                  <label className="inline-flex items-center gap-1 px-3 h-7 bg-white border border-zinc-300 hover:bg-zinc-100 rounded-md text-xs cursor-pointer">
                    {uploading ? "Uploading…" : "+ Add file"}
                    <input type="file" hidden onChange={(e) => uploadBill(e, "edit")} disabled={uploading} />
                  </label>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(editExp.supporting_documents || []).map((id, idx) => (
                    <button key={id} onClick={() => downloadDoc(id)} className="inline-flex items-center gap-1 text-[11px] bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded text-emerald-800 hover:bg-emerald-100">
                      <Download size={10} /> Bill #{idx + 1}
                    </button>
                  ))}
                  {!(editExp.supporting_documents || []).length && <div className="text-[11px] text-zinc-400">No files.</div>}
                </div>
              </div>

              <div className="col-span-2"><Label>Reason for change (audit)</Label><Input value={editExp._reason || ""} onChange={(e) => setEditExp({ ...editExp, _reason: e.target.value })} placeholder="e.g., Correction of vendor invoice amount" data-testid="ee-reason" /></div>
            </div>
          )}
          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={() => deleteExp(editExp)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-expense-btn">
              Delete
            </Button>
            <Button onClick={saveEditExp} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-edit-expense">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// =========================== PETTY CASH SUB-COMPONENT ===========================
function PettyCashPanel({ projects, projectId, accounts }) {
  const qc = useQueryClient();
  const [accOpen, setAccOpen] = useState(false);
  const [accForm, setAccForm] = useState({ name: "", project_id: "", custodian_name: "", opening_balance: 0, notes: "" });
  const [txnOpen, setTxnOpen] = useState(false);
  const [txnForm, setTxnForm] = useState({ account_id: "", type: "topup", amount: 0, date: new Date().toISOString().slice(0, 10), source: "bank", reference_no: "", notes: "" });
  const [ledgerAccountId, setLedgerAccountId] = useState(null);

  const { data: ledger } = useQuery({
    queryKey: ["petty-ledger", ledgerAccountId],
    queryFn: async () => (await api.get(`/petty-cash/accounts/${ledgerAccountId}/ledger`)).data,
    enabled: !!ledgerAccountId,
  });

  async function saveAcc() {
    if (!accForm.name) return toast.error("Name required");
    try {
      await api.post("/petty-cash/accounts", { ...accForm, opening_balance: +accForm.opening_balance, project_id: accForm.project_id || null });
      toast.success("Petty cash account created");
      qc.invalidateQueries({ queryKey: ["petty-accounts"] });
      setAccOpen(false);
      setAccForm({ name: "", project_id: "", custodian_name: "", opening_balance: 0, notes: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function saveTxn() {
    if (!txnForm.account_id) return toast.error("Select account");
    if (!txnForm.amount || +txnForm.amount <= 0) return toast.error("Enter valid amount");
    try {
      await api.post("/petty-cash/transactions", { ...txnForm, amount: +txnForm.amount });
      toast.success(`${txnForm.type === "topup" ? "Top-up" : "Withdrawal"} recorded`);
      qc.invalidateQueries({ queryKey: ["petty-accounts"] });
      if (ledgerAccountId) qc.invalidateQueries({ queryKey: ["petty-ledger", ledgerAccountId] });
      setTxnOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="space-y-4">
      <Card className="bg-white border-zinc-200">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="font-heading text-base flex items-center gap-2"><Wallet size={16} className="text-emerald-700" /> Petty Cash Registers</CardTitle>
          <div className="flex gap-2">
            <Dialog open={txnOpen} onOpenChange={setTxnOpen}>
              <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="petty-txn-btn"><ArrowUpCircle size={13} className="mr-1" />Top-up / Withdraw</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Petty Cash Transaction</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Account</Label>
                    <Select value={txnForm.account_id} onValueChange={(v) => setTxnForm({ ...txnForm, account_id: v })}>
                      <SelectTrigger data-testid="petty-txn-account"><SelectValue placeholder="Choose account" /></SelectTrigger>
                      <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · Bal {inr(a.balance)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Type</Label>
                    <Select value={txnForm.type} onValueChange={(v) => setTxnForm({ ...txnForm, type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="topup">Top-up (Cash In)</SelectItem>
                        <SelectItem value="withdraw">Withdraw (Cash Out)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Amount (₹)</Label><Input type="number" value={txnForm.amount} onChange={(e) => setTxnForm({ ...txnForm, amount: e.target.value })} data-testid="petty-txn-amount" /></div>
                  <div><Label>Date</Label><Input type="date" value={txnForm.date} onChange={(e) => setTxnForm({ ...txnForm, date: e.target.value })} /></div>
                  <div><Label>Source</Label>
                    <Select value={txnForm.source} onValueChange={(v) => setTxnForm({ ...txnForm, source: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank">Bank Withdrawal</SelectItem>
                        <SelectItem value="cash">Cash Deposit</SelectItem>
                        <SelectItem value="manual">Manual Adjustment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Reference No.</Label><Input value={txnForm.reference_no} onChange={(e) => setTxnForm({ ...txnForm, reference_no: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Notes</Label><Input value={txnForm.notes} onChange={(e) => setTxnForm({ ...txnForm, notes: e.target.value })} /></div>
                </div>
                <DialogFooter><Button onClick={saveTxn} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-petty-txn">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={accOpen} onOpenChange={setAccOpen}>
              <DialogTrigger asChild><Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-petty-btn"><Plus size={14} className="mr-1" /> New Register</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">New Petty Cash Register</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Name *</Label><Input value={accForm.name} onChange={(e) => setAccForm({ ...accForm, name: e.target.value })} placeholder="e.g. Site A - Cash Box" data-testid="petty-name" /></div>
                  <div><Label>Project (optional)</Label>
                    <Select value={accForm.project_id || " "} onValueChange={(v) => setAccForm({ ...accForm, project_id: v.trim() })}>
                      <SelectTrigger><SelectValue placeholder="Company-wide" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value=" ">Company-wide</SelectItem>
                        {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Custodian</Label><Input value={accForm.custodian_name} onChange={(e) => setAccForm({ ...accForm, custodian_name: e.target.value })} /></div>
                  <div><Label>Opening Balance (₹)</Label><Input type="number" value={accForm.opening_balance} onChange={(e) => setAccForm({ ...accForm, opening_balance: e.target.value })} data-testid="petty-opening" /></div>
                  <div className="col-span-2"><Label>Notes</Label><Input value={accForm.notes} onChange={(e) => setAccForm({ ...accForm, notes: e.target.value })} /></div>
                </div>
                <DialogFooter><Button onClick={saveAcc} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-petty-acc">Create</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-zinc-50"><TableRow>
              <TableHead>Register</TableHead>
              <TableHead>Project</TableHead>
              <TableHead>Custodian</TableHead>
              <TableHead className="text-right">Opening</TableHead>
              <TableHead className="text-right">Current Balance</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {accounts.map((a) => (
                <TableRow key={a.id} className="data-row" data-testid={`petty-row-${a.id}`}>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell className="text-xs">{projects.find((p) => p.id === a.project_id)?.name || <span className="text-zinc-400">Company-wide</span>}</TableCell>
                  <TableCell className="text-xs">{a.custodian_name || "—"}</TableCell>
                  <TableCell className="text-right font-mono">{inr(a.opening_balance)}</TableCell>
                  <TableCell className={`text-right font-mono font-semibold ${a.balance < 0 ? "text-red-600" : "text-emerald-700"}`}>{inr(a.balance)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => setLedgerAccountId(a.id)} data-testid={`petty-ledger-${a.id}`}>Ledger</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!accounts.length && <TableRow><TableCell colSpan={6} className="text-center text-sm text-zinc-500 py-10">No petty cash registers yet. Create one to start tracking cash.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Ledger Dialog */}
      <Dialog open={!!ledgerAccountId} onOpenChange={(o) => !o && setLedgerAccountId(null)}>
        <DialogContent className="max-w-3xl" data-testid="petty-ledger-dialog">
          <DialogHeader><DialogTitle className="font-heading">Petty Cash Ledger — {ledger?.account?.name}</DialogTitle></DialogHeader>
          <div className="text-sm text-zinc-500 mb-2">Current Balance: <span className="font-mono font-bold text-emerald-700">{inr(ledger?.current_balance || 0)}</span></div>
          <div className="max-h-[400px] overflow-y-auto border rounded">
            <Table>
              <TableHeader className="bg-zinc-50 sticky top-0"><TableRow>
                <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Source</TableHead><TableHead>Notes</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Balance</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(ledger?.transactions || []).map((t, i) => (
                  <TableRow key={t.id || `${t.date}-${t.type}-${i}`} className="data-row">
                    <TableCell className="font-mono text-xs">{fmtDate(t.date)}</TableCell>
                    <TableCell>
                      {t.type === "topup" ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><ArrowUpCircle size={12} /> Top-up</span>
                        : t.type === "withdraw" ? <span className="inline-flex items-center gap-1 text-red-600 text-xs"><ArrowDownCircle size={12} /> Withdraw</span>
                          : <span className="text-xs text-zinc-500">{t.type}</span>}
                    </TableCell>
                    <TableCell className="text-xs">{t.source || "—"}</TableCell>
                    <TableCell className="text-xs text-zinc-500">{t.notes || t.reference_no || "—"}</TableCell>
                    <TableCell className={`text-right font-mono ${t.type === "topup" ? "text-emerald-700" : t.type === "withdraw" ? "text-red-600" : ""}`}>{inr(t.amount)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{inr(t.balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// =========================== FINANCE SETTINGS BUTTON (Admin only) ===========================
function SettingsButton({ finSettings }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [attach, setAttach] = useState(finSettings?.expense_attachment_threshold ?? 5000);
  const [approval, setApproval] = useState(finSettings?.expense_approval_threshold ?? 50000);

  React.useEffect(() => {
    if (finSettings) {
      setAttach(finSettings.expense_attachment_threshold);
      setApproval(finSettings.expense_approval_threshold);
    }
  }, [finSettings]);

  async function save() {
    try {
      await api.patch("/finance/settings/expense_attachment_threshold", { value: +attach });
      await api.patch("/finance/settings/expense_approval_threshold", { value: +approval });
      toast.success("Thresholds updated");
      qc.invalidateQueries({ queryKey: ["finance-settings"] });
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="finance-settings-btn"><Cog size={13} className="mr-1" /> Thresholds</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Finance Thresholds</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Bill Mandatory Above (₹)</Label>
            <Input type="number" value={attach} onChange={(e) => setAttach(e.target.value)} data-testid="setting-attach" />
            <div className="text-[11px] text-zinc-500 mt-1">Any expense of this amount or higher must have a bill attachment.</div>
          </div>
          <div>
            <Label>Approval Required Above (₹)</Label>
            <Input type="number" value={approval} onChange={(e) => setApproval(e.target.value)} data-testid="setting-approval" />
            <div className="text-[11px] text-zinc-500 mt-1">Non-Admin/Manager users need Admin/Manager approval above this amount.</div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-settings">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
